import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Clock, AlertCircle, CheckCircle, MessageSquare, User, Building,
  Loader2, Search, Filter, ChevronRight, Star, FileText, Send
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';

const STATUS_COLORS = {
  new: 'bg-[var(--gold)]/20 text-[var(--gold)]',
  under_review: 'bg-yellow-100 text-yellow-800',
  responded: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800'
};

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  normal: 'bg-[var(--gold)]/20 text-[var(--gold)]',
  high: 'bg-orange-100 text-orange-600',
  urgent: 'bg-red-100 text-red-600'
};

const StoneAdvisorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  useEffect(() => {
    fetchDashboard();
    fetchRequests();
  }, [statusFilter, priorityFilter]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/advisor/dashboard`, { withCredentials: true });
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      if (error.response?.status === 403) {
        toast.error('Not authorized as Stone Advisor');
        navigate('/dashboard');
      }
    }
  };

  const fetchRequests = async () => {
    try {
      let url = `${API}/advisor/requests?limit=50`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (priorityFilter) url += `&priority=${priorityFilter}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setRequests(response.data.requests);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="max-w-[1600px] mx-auto px-6 py-8 pt-24">
        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white border border-border p-4">
              <div className="text-3xl font-bold text-[var(--gold)]">{dashboard.total_new}</div>
              <div className="text-sm text-muted-foreground">New Requests</div>
            </div>
            <div className="bg-white border border-border p-4">
              <div className="text-3xl font-bold text-yellow-600">{dashboard.total_under_review}</div>
              <div className="text-sm text-muted-foreground">Under Review</div>
            </div>
            <div className="bg-white border border-border p-4">
              <div className="text-3xl font-bold text-green-600">{dashboard.total_responded}</div>
              <div className="text-sm text-muted-foreground">Responded</div>
            </div>
            <div className="bg-white border border-border p-4">
              <div className="text-3xl font-bold">{dashboard.total_today}</div>
              <div className="text-sm text-muted-foreground">Today</div>
            </div>
            <div className="bg-white border border-border p-4">
              <div className="text-3xl font-bold text-red-600">{dashboard.high_priority}</div>
              <div className="text-sm text-muted-foreground">High Priority</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="under_review">Under Review</SelectItem>
              <SelectItem value="responded">Responded</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Priorities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Requests Table */}
        <div className="bg-white border border-border">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  <th className="p-4 text-left text-sm font-medium">Image</th>
                  <th className="p-4 text-left text-sm font-medium">Requester</th>
                  <th className="p-4 text-left text-sm font-medium">Project</th>
                  <th className="p-4 text-left text-sm font-medium">Application</th>
                  <th className="p-4 text-left text-sm font-medium">AI Top Match</th>
                  <th className="p-4 text-left text-sm font-medium">Status</th>
                  <th className="p-4 text-left text-sm font-medium">Priority</th>
                  <th className="p-4 text-left text-sm font-medium">Date</th>
                  <th className="p-4 text-left text-sm font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-muted-foreground">
                      No requests found
                    </td>
                  </tr>
                ) : (
                  requests.map(req => (
                    <tr 
                      key={req.request_id} 
                      className="border-b border-border hover:bg-secondary/20 cursor-pointer"
                      onClick={() => navigate(`/advisor/requests/${req.request_id}`)}
                    >
                      <td className="p-4">
                        <div className="w-16 h-16 bg-secondary overflow-hidden">
                          {req.uploaded_image_base64 ? (
                            <img 
                              src={`data:image/jpeg;base64,${req.uploaded_image_base64.slice(0, 100)}...`}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                              No img
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="font-medium text-sm">{req.user_name || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground">{req.user_company}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">{req.project_name || '—'}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm capitalize">{req.application_area || '—'}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          {req.ai_suggestions?.[0]?.material_name || '—'}
                        </div>
                        {req.ai_suggestions?.[0]?.similarity_score && (
                          <div className="text-xs text-muted-foreground">
                            {req.ai_suggestions[0].similarity_score}% match
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${STATUS_COLORS[req.status]}`}>
                          {req.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${PRIORITY_COLORS[req.priority]}`}>
                          {req.priority}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-muted-foreground">
                          {new Date(req.submitted_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="p-4">
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

// Request Detail Page Component
export const AdvisorRequestDetail = () => {
  const { requestId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showResponseEditor, setShowResponseEditor] = useState(false);
  const [internalNote, setInternalNote] = useState('');
  
  // Response form
  const [responseForm, setResponseForm] = useState({
    likely_material_id: '',
    alternative_material_ids: [],
    confidence_level: 'medium',
    response_notes: '',
    application_note: '',
    suggested_next_action: 'view'
  });

  useEffect(() => {
    fetchRequest();
    fetchMaterials();
    fetchTemplates();
  }, [requestId]);

  const fetchRequest = async () => {
    try {
      const response = await axios.get(`${API}/advisor/requests/${requestId}`, { withCredentials: true });
      setRequest(response.data);
    } catch (error) {
      console.error('Failed to fetch request:', error);
      toast.error('Request not found');
      navigate('/advisor');
    } finally {
      setLoading(false);
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/materials?limit=500`, { withCredentials: true });
      const materialList = Array.isArray(response.data) ? response.data : response.data.materials || [];
      setMaterials(materialList);
    } catch (error) {
      console.error('Failed to fetch materials:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/advisor/templates`, { withCredentials: true });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const updateStatus = async (status) => {
    try {
      await axios.put(`${API}/advisor/requests/${requestId}`, { status }, { withCredentials: true });
      setRequest(prev => ({ ...prev, status }));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updatePriority = async (priority) => {
    try {
      await axios.put(`${API}/advisor/requests/${requestId}`, { priority }, { withCredentials: true });
      setRequest(prev => ({ ...prev, priority }));
      toast.success('Priority updated');
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const addInternalNote = async () => {
    if (!internalNote.trim()) return;
    try {
      await axios.post(`${API}/advisor/requests/${requestId}/internal-note`, {
        note_text: internalNote
      }, { withCredentials: true });
      toast.success('Note added');
      setInternalNote('');
      fetchRequest();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const applyTemplate = (template) => {
    setResponseForm(prev => ({
      ...prev,
      response_notes: template.response_notes,
      application_note: template.application_note || ''
    }));
  };

  const submitResponse = async () => {
    if (!responseForm.likely_material_id) {
      toast.error('Please select a likely material');
      return;
    }
    if (!responseForm.response_notes.trim()) {
      toast.error('Please add response notes');
      return;
    }

    try {
      await axios.post(`${API}/advisor/requests/${requestId}/response`, responseForm, { withCredentials: true });
      toast.success('Response submitted!');
      setShowResponseEditor(false);
      fetchRequest();
    } catch (error) {
      toast.error('Failed to submit response');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!request) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-border">
        <div className="max-w-[1600px] mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/advisor')} className="text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="heading-display text-lg font-medium">Request #{request.request_id.slice(-8)}</h1>
              <span className={`px-2 py-1 text-xs font-medium rounded ${STATUS_COLORS[request.status]}`}>
                {request.status.replace('_', ' ')}
              </span>
            </div>
            {!request.response && (
              <Button onClick={() => setShowResponseEditor(true)} className="btn-primary">
                <Send className="w-4 h-4 mr-2" /> Create Response
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Uploaded Image */}
            <div className="bg-white border border-border p-6">
              <h2 className="font-medium mb-4">Uploaded Image</h2>
              {request.uploaded_image_base64 && (
                <img 
                  src={`data:image/jpeg;base64,${request.uploaded_image_base64}`}
                  alt="Uploaded stone"
                  className="max-w-full max-h-96 object-contain mx-auto"
                />
              )}
            </div>

            {/* AI Suggestions */}
            <div className="bg-white border border-border p-6">
              <h2 className="font-medium mb-4">AI Suggested Matches</h2>
              <div className="space-y-3">
                {request.ai_suggestions?.map((suggestion, idx) => (
                  <div 
                    key={suggestion.material_id}
                    className="flex items-center gap-4 p-3 bg-secondary/30"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-secondary font-bold">
                      #{idx + 1}
                    </div>
                    {suggestion.thumbnail_url && (
                      <img src={suggestion.thumbnail_url} alt="" className="w-12 h-12 object-cover" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{suggestion.material_name}</p>
                      <p className="text-sm text-muted-foreground">{suggestion.stone_type}</p>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${
                        suggestion.similarity_score >= 75 ? 'text-green-600' : 
                        suggestion.similarity_score >= 50 ? 'text-yellow-600' : 'text-muted-foreground'
                      }`}>
                        {suggestion.similarity_score}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* User Notes */}
            {request.notes && (
              <div className="bg-white border border-border p-6">
                <h2 className="font-medium mb-4">User Notes</h2>
                <p className="text-muted-foreground">{request.notes}</p>
              </div>
            )}

            {/* Existing Response */}
            {request.response && (
              <div className="bg-green-50 border border-green-200 p-6">
                <h2 className="font-medium mb-4 text-green-800">Response Submitted</h2>
                <div className="space-y-3">
                  <p><strong>Likely Material:</strong> {request.response.likely_material_id}</p>
                  <p><strong>Confidence:</strong> {request.response.confidence_level}</p>
                  <p><strong>Notes:</strong> {request.response.response_notes}</p>
                  {request.response.application_note && (
                    <p><strong>Application Note:</strong> {request.response.application_note}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Request Info */}
            <div className="bg-white border border-border p-6">
              <h2 className="font-medium mb-4">Request Information</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Requester:</span>
                  <p className="font-medium">{request.user_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Email:</span>
                  <p>{request.user_email}</p>
                </div>
                {request.user_company && (
                  <div>
                    <span className="text-muted-foreground">Company:</span>
                    <p>{request.user_company}</p>
                  </div>
                )}
                {request.project_name && (
                  <div>
                    <span className="text-muted-foreground">Project:</span>
                    <p>{request.project_name}</p>
                  </div>
                )}
                {request.application_area && (
                  <div>
                    <span className="text-muted-foreground">Application:</span>
                    <p className="capitalize">{request.application_area}</p>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">Submitted:</span>
                  <p>{new Date(request.submitted_at).toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white border border-border p-6">
              <h2 className="font-medium mb-4">Actions</h2>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm mb-2 block">Status</Label>
                  <Select value={request.status} onValueChange={updateStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="under_review">Under Review</SelectItem>
                      <SelectItem value="responded">Responded</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm mb-2 block">Priority</Label>
                  <Select value={request.priority} onValueChange={updatePriority}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Internal Notes */}
            <div className="bg-white border border-border p-6">
              <h2 className="font-medium mb-4">Internal Notes</h2>
              <div className="space-y-3">
                <Textarea
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  placeholder="Add internal note..."
                  className="input-field min-h-[80px]"
                />
                <Button variant="outline" size="sm" onClick={addInternalNote}>
                  Add Note
                </Button>
                
                {request.internal_notes?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {request.internal_notes.map(note => (
                      <div key={note.note_id} className="p-2 bg-secondary/30 text-sm">
                        <p className="text-muted-foreground">{note.note_text}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {note.advisor_name} - {new Date(note.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Response Editor Dialog */}
      <Dialog open={showResponseEditor} onOpenChange={setShowResponseEditor}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading-display text-xl">Create Advisor Response</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Templates */}
            <div>
              <Label className="mb-2 block">Quick Templates</Label>
              <div className="flex flex-wrap gap-2">
                {templates.map(template => (
                  <Button
                    key={template.template_id}
                    variant="outline"
                    size="sm"
                    onClick={() => applyTemplate(template)}
                  >
                    {template.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Likely Material */}
            <div>
              <Label className="mb-2 block">Likely Material Match *</Label>
              <Select 
                value={responseForm.likely_material_id} 
                onValueChange={(v) => setResponseForm(prev => ({ ...prev, likely_material_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select material..." />
                </SelectTrigger>
                <SelectContent>
                  {materials.map(mat => (
                    <SelectItem key={mat.material_id} value={mat.material_id}>
                      {mat.name} ({mat.stone_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Confidence */}
            <div>
              <Label className="mb-2 block">Confidence Level</Label>
              <Select 
                value={responseForm.confidence_level} 
                onValueChange={(v) => setResponseForm(prev => ({ ...prev, confidence_level: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Response Notes */}
            <div>
              <Label className="mb-2 block">Response Notes *</Label>
              <Textarea
                value={responseForm.response_notes}
                onChange={(e) => setResponseForm(prev => ({ ...prev, response_notes: e.target.value }))}
                placeholder="Your assessment and recommendations..."
                className="input-field min-h-[120px]"
              />
            </div>

            {/* Application Note */}
            <div>
              <Label className="mb-2 block">Application Note (optional)</Label>
              <Textarea
                value={responseForm.application_note}
                onChange={(e) => setResponseForm(prev => ({ ...prev, application_note: e.target.value }))}
                placeholder="Specific notes about application, finish, suitability..."
                className="input-field min-h-[80px]"
              />
            </div>

            {/* Suggested Action */}
            <div>
              <Label className="mb-2 block">Suggested Next Action</Label>
              <Select 
                value={responseForm.suggested_next_action} 
                onValueChange={(v) => setResponseForm(prev => ({ ...prev, suggested_next_action: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">View Material</SelectItem>
                  <SelectItem value="save">Save Material</SelectItem>
                  <SelectItem value="sample">Request Sample</SelectItem>
                  <SelectItem value="quote">Request Quote</SelectItem>
                  <SelectItem value="showroom">Visit Showroom</SelectItem>
                  <SelectItem value="advisor">Speak with Advisor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={submitResponse} className="w-full btn-primary">
              <Send className="w-4 h-4 mr-2" /> Submit Response
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoneAdvisorDashboard;
